<template>
  <AppPageContainer :custom="1600" :min-height="0" class="sp-page">
    <UiPageHeader design="new" title="Shift Coverage Dashboard" compact />
    <ShiftPlannerNav />
    <div class="ui-page">
      <AppFilterToolbar>
        <template #primary>
          <app-date-picker
            v-model="weekStart"
            design="new"
            :input-attr="{ id: 'shift-planner-week', 'aria-label': 'Week of' }"
            :clearable="false"
            format="DD MMM YYYY"
            value-type="YYYY-MM-DD"
            @input="selectWeekDate"
          />
        </template>
        <AppIconButton label="Previous week" tooltip-placement="left" :disabled="Boolean(weekNavigation)" :aria-busy="weekNavigation === 'previous' ? 'true' : 'false'" @click="shiftWeek(-1)"><i :class="weekNavigation === 'previous' ? 'pi pi-spin pi-spinner' : 'pi pi-chevron-left'" aria-hidden="true" /></AppIconButton>
        <AppIconButton label="Next week" tooltip-placement="left" :disabled="Boolean(weekNavigation)" :aria-busy="weekNavigation === 'next' ? 'true' : 'false'" @click="shiftWeek(1)"><i :class="weekNavigation === 'next' ? 'pi pi-spin pi-spinner' : 'pi pi-chevron-right'" aria-hidden="true" /></AppIconButton>
        <AppIconButton label="Current week" tooltip-placement="left" :disabled="Boolean(weekNavigation)" :aria-busy="weekNavigation === 'current' ? 'true' : 'false'" @click="goToCurrentWeek"><i :class="weekNavigation === 'current' ? 'pi pi-spin pi-spinner' : 'pi pi-calendar'" aria-hidden="true" /></AppIconButton>
      </AppFilterToolbar>
      <AppErrorState v-if="error" :message="error" @retry="load" />
      <AppContentSkeleton v-if="loading && !dashboard" layout="block" label="Loading coverage" />

    <template v-if="dashboard">
      <section class="sp-grid sp-dashboard-metrics" aria-label="Weekly coverage summary">
        <UiMetricCard
          design="new"
          padding="compact"
          label="Shift cells"
          :value="dashboard.summary.shift_cells"
          meta="Six shifts across seven days"
          tone="blue"
        />
        <UiMetricCard
          design="new"
          padding="compact"
          label="Coverage deficits"
          :value="dashboard.summary.deficit_cells"
          meta="Below the configured minimum"
          :tone="dashboard.summary.deficit_cells ? 'red' : 'green'"
          :status="dashboard.summary.deficit_cells ? 'Open the red cells and add available managers.' : ''"
        />
        <UiMetricCard
          design="new"
          padding="compact"
          label="Covered shifts"
          :value="coveredCount"
          meta="At or above minimum staffing"
          tone="green"
        />
        <UiMetricCard
          design="new"
          padding="compact"
          label="Full shifts"
          :value="fullCount"
          meta="At configured maximum capacity"
          :tone="fullCount ? 'blue' : 'neutral'"
        />
      </section>

      <AppCard design="new" class="sp-coverage-card" :aria-busy="loading ? 'true' : 'false'">
        <div class="sp-header sp-coverage-header">
          <div>
            <h2>{{ formatRange }}</h2>
            <div class="sp-muted">
              Select a shift cell to inspect its roster and update staffing.
            </div>
          </div>
          <LegendDropdown :items="legendItems" align="right" />
        </div>

        <div class="sp-coverage-navigation">
          <button
            class="sp-coverage-nav-button"
            type="button"
            aria-label="Previous week"
            :disabled="Boolean(weekNavigation)"
            :aria-busy="weekNavigation === 'previous' ? 'true' : 'false'"
            @click="shiftWeek(-1)"
          >
            <i v-if="weekNavigation === 'previous'" class="pi pi-spin pi-spinner" aria-hidden="true" />
            <svg v-else viewBox="0 0 24 24" aria-hidden="true">
              <path d="m15 5-7 7 7 7" />
            </svg>
          </button>
          <div class="sp-coverage-scroll">
            <div class="sp-coverage-matrix" :style="gridStyle">
              <div class="sp-coverage-matrix__corner">Shift</div>
              <div
                v-for="day in weekDays"
                :key="`${day.date}-head`"
                class="sp-coverage-matrix__day"
                :class="{
                  'is-weekend': day.isWeekend,
                  'is-today': day.date === todayDate,
                }"
              >
                <strong>{{ day.weekday }}</strong>
                <span>{{ day.dayMonth }}</span>
              </div>

              <template v-for="template in templates">
                <div :key="`${template.code}-label`" class="sp-coverage-matrix__shift">
                  <i :class="`is-${template.kind}`" />
                  <div>
                    <strong>{{ template.code }}</strong>
                    <span>{{ template.start_local_time }}–{{ template.end_local_time }}</span>
                  </div>
                </div>
                <button
                  v-for="day in weekDays"
                  :key="`${template.code}-${day.date}`"
                  class="sp-coverage-cell"
                  :class="coverageCellClasses(cellFor(template.code, day.date), day)"
                  type="button"
                  :disabled="!cellFor(template.code, day.date)"
                  :aria-label="coverageCellAria(cellFor(template.code, day.date), template, day)"
                  @click="openCell(cellFor(template.code, day.date))"
                >
                  <template v-if="cellFor(template.code, day.date)">
                    <strong>
                      {{ cellFor(template.code, day.date).assigned }} /
                      {{ cellFor(template.code, day.date).min }}
                    </strong>
                    <span>max {{ cellFor(template.code, day.date).max }}</span>
                  </template>
                  <span v-else>—</span>
                </button>
              </template>
            </div>
          </div>
          <button
            class="sp-coverage-nav-button"
            type="button"
            aria-label="Next week"
            :disabled="Boolean(weekNavigation)"
            :aria-busy="weekNavigation === 'next' ? 'true' : 'false'"
            @click="shiftWeek(1)"
          >
            <i v-if="weekNavigation === 'next'" class="pi pi-spin pi-spinner" aria-hidden="true" />
            <svg v-else viewBox="0 0 24 24" aria-hidden="true">
              <path d="m9 5 7 7-7 7" />
            </svg>
          </button>
        </div>
      </AppCard>

      <AppCard design="new" class="sp-chart-card">
        <div class="sp-chart-card__heading">
          <div>
            <h2>Demand vs planned coverage</h2>
            <div class="sp-muted">{{ analytics.note }}</div>
          </div>
        </div>
        <AppErrorState v-if="analyticsError" :message="analyticsError" @retry="loadAnalytics" />
        <div v-else class="sp-chart-shell">
          <highcharts v-if="chartOptions" :options="chartOptions" />
        </div>
      </AppCard>
    </template>

    </div>

    <modal
      name="supervisor-roster"
      :width="1180"
      height="auto"
      :adaptive="true"
      :scrollable="true"
      :click-to-close="false"
      class="sp-planner-modal"
      @opened="focusRosterModal"
    >
      <div
        class="sp-modal sp-roster-modal"
        :class="{ 'sp-roster-modal--editable': rosterEditable }"
        role="dialog"
        aria-modal="true"
        aria-labelledby="supervisor-roster-title"
      >
        <ModalHeader
          ref="rosterHeader"
          design="new"
          title-id="supervisor-roster-title"
          eyebrow="Shift roster"
          :title="rosterModalTitle"
          @close="closeCell"
        />
        <div class="sp-modal__body">
          <div v-if="cellLoading" class="sp-skeleton" />
          <template v-else>
            <div class="sp-roster-overview">
              <UiStatusBadge
                design="new"
                :label="rosterCoverageLabel"
                :tone="coverageTone(selectedCell && selectedCell.state)"
              />
              <span v-if="selectedCell">
                {{ selectedCell.assigned }} assigned · minimum {{ selectedCell.min }} · maximum
                {{ selectedCell.max }}
              </span>
            </div>

            <div v-if="modalError" class="sp-error" role="alert">{{ modalError }}</div>

            <div class="sp-roster-columns">
              <div class="sp-roster-columns__lists">
                <section class="sp-roster-section">
                  <div class="sp-roster-section__heading">
                    <div>
                      <h3>Scheduled managers</h3>
                      <span>{{ roster.length }} in roster</span>
                    </div>
                  </div>
                  <div v-if="roster.length" class="sp-roster-list">
                    <div
                      v-for="assignment in roster"
                      :key="assignment.id"
                      class="sp-roster-item"
                    >
                      <div class="sp-roster-item__manager">
                        <strong>{{ assignment.user.name }}</strong>
                        <LanguageSummary :languages="assignment.user.languages" />
                      </div>
                      <AppButton
                        v-if="!saving && canRemoveAssignment(assignment)"
                        design="new"
                        theme="error"
                        size="small"
                        @click.native="removeAssignment(assignment)"
                      >
                        Remove
                      </AppButton>
                    </div>
                  </div>
                  <div v-else class="sp-empty sp-empty--compact">
                    <strong>No managers assigned</strong>
                    <p>{{ rosterEditable
                      ? 'Add an available manager from the form.'
                      : 'This roster is read-only.' }}</p>
                  </div>
                </section>

                <section
                  v-if="rosterEditable && unavailableParticipants.length"
                  class="sp-roster-section"
                >
                  <div class="sp-roster-section__heading">
                    <div>
                      <h3>Unavailable managers</h3>
                      <span>Why they cannot be assigned to this shift</span>
                    </div>
                  </div>
                  <div class="sp-unavailable-list">
                    <div
                      v-for="participant in unavailableParticipants"
                      :key="participant.id"
                      class="sp-unavailable-item"
                    >
                      <div>
                        <strong>{{ participant.user.name }}</strong>
                        <LanguageSummary :languages="participant.user.languages" />
                      </div>
                      <span>{{ unavailableReason(participant) }}</span>
                    </div>
                  </div>
                </section>
              </div>

              <aside class="sp-roster-editor" aria-label="Roster editor">
                <template v-if="rosterEditable">
                  <label v-if="modalReasonRequired" for="shift-planner-roster-reason">
                    <span class="sp-field-label">
                      Supervisor change reason<span class="sp-required-marker" aria-hidden="true">*</span>
                    </span>
                    <app-input
                      v-model="overrideReason"
                      id="shift-planner-roster-reason"
                      design="new"
                      :error="!hasOverrideReason"
                      :aria-invalid="!hasOverrideReason"
                      aria-required="true"
                      placeholder="Required for current-week changes"
                    />
                  </label>

                  <section class="sp-roster-section sp-roster-section--add">
                    <div class="sp-roster-section__heading">
                      <div>
                        <h3>Add manager</h3>
                        <span>{{ availableParticipants.length }} available</span>
                      </div>
                    </div>
                    <div class="sp-roster-form">
                      <label for="shift-planner-roster-manager">
                        <span class="sp-field-label">
                          Manager<span class="sp-required-marker" aria-hidden="true">*</span>
                        </span>
                        <app-select
                          v-model="participantId"
                          design="new"
                          class="sp-select--manager"
                          input-id="shift-planner-roster-manager"
                          append-to-body
                          :calculate-position="positionRosterSelect"
                          :options="availableParticipants"
                          :reduce="(participant) => participant.id"
                          :display-field="participantLabel"
                          :get-option-label="participantLabel"
                          :clearable="false"
                          aria-required="true"
                          placeholder="Select manager"
                        />
                      </label>
                      <div
                        v-if="selectedParticipantContext && selectedParticipantContext.leave_status === 'requested'"
                        class="sp-roster-note"
                      >
                        This manager has a pending leave request for this date.
                      </div>
                      <AppButton
                        design="new"
                        :disabled="!canAddAssignment"
                        @click.native="addAssignment"
                      >
                        Add to shift
                      </AppButton>
                    </div>
                  </section>
                </template>
                <div v-else class="sp-roster-read-only" role="status">
                  <svg viewBox="0 0 24 24" aria-hidden="true">
                    <rect x="5" y="10" width="14" height="10" rx="2" />
                    <path d="M8 10V7a4 4 0 0 1 8 0v3" />
                  </svg>
                  <strong>Read-only schedule</strong>
                  <p>{{ modalLockedReason }}</p>
                  <span>No roster changes are available for this shift.</span>
                </div>
              </aside>
            </div>
          </template>
        </div>
        <footer class="sp-modal__footer">
          <span class="sp-roster-save-state">{{ saving
            ? 'Saving changes…'
            : rosterEditable
              ? 'Changes save automatically'
              : 'Read-only schedule' }}</span>
          <AppButton design="new" theme="default" :disabled="saving" @click.native="closeCell">
            Done
          </AppButton>
        </footer>
      </div>
    </modal>
  </AppPageContainer>
</template>

<script>
import {
  addDays,
  addWeeks,
  endOfWeek,
  format,
  parseISO,
  startOfWeek,
} from 'date-fns'
import { Chart } from 'highcharts-vue'
import { createPopper } from '@popperjs/core'
import AppCard from '@/components/AppCard.vue'
import AppContentSkeleton from '@/components/AppContentSkeleton.vue'
import AppErrorState from '@/components/AppErrorState.vue'
import LanguageSummary from './LanguageSummary.vue'
import LegendDropdown from './LegendDropdown.vue'
import ModalHeader from '@/components/AppModalHeader.vue'
import ShiftPlannerNav from './PlannerNav.vue'
import UiMetricCard from '@/components/AppMetricCard.vue'
import UiPageHeader from '@/components/AppPageHeader.vue'
import UiStatusBadge from '@/components/AppStatusBadge.vue'
import {
  getShiftPlannerDashboard,
  getShiftPlannerDashboardAnalytics,
  getShiftPlannerRoster,
  postShiftPlannerAssignment,
  postShiftPlannerAssignmentCancel,
} from '@/http'

export default {
  name: 'ShiftPlannerDashboard',
  components: {
    AppCard,
    AppContentSkeleton,
    AppErrorState,
    LanguageSummary,
    LegendDropdown,
    ModalHeader,
    ShiftPlannerNav,
    UiMetricCard,
    UiPageHeader,
    UiStatusBadge,
    highcharts: Chart,
  },
  data() {
    const today = new Date()
    return {
      weekStart: format(startOfWeek(today, { weekStartsOn: 1 }), 'yyyy-MM-dd'),
      dashboard: null,
      analytics: {},
      selectedCell: null,
      selectedShift: null,
      selectedParticipants: [],
      participantContexts: {},
      participantId: null,
      overrideReason: '',
      loading: false,
      weekNavigation: '',
      cellLoading: false,
      saving: false,
      error: '',
      analyticsError: '',
      modalError: '',
    }
  },
  computed: {
    month() {
      return format(parseISO(this.weekStart), 'yyyy-MM')
    },
    weekEnd() {
      return format(
        endOfWeek(parseISO(this.weekStart), { weekStartsOn: 1 }),
        'yyyy-MM-dd'
      )
    },
    weekDays() {
      return Array.from({ length: 7 }, (_, index) => {
        const date = addDays(parseISO(this.weekStart), index)
        return {
          date: format(date, 'yyyy-MM-dd'),
          weekday: format(date, 'EEE'),
          dayMonth: format(date, 'd MMM'),
          isWeekend: [0, 6].includes(date.getDay()),
        }
      })
    },
    todayDate() {
      return format(new Date(), 'yyyy-MM-dd')
    },
    templates() {
      return this.dashboard ? this.dashboard.templates || [] : []
    },
    legendItems() {
      return [
        ...this.templates.map((template) => ({
          key: `template-${template.code}`,
          tone: template.kind,
          label: template.code,
          description: `${template.start_local_time}–${template.end_local_time}`,
        })),
        {
          key: 'deficit',
          tone: 'deficit',
          label: 'Below min',
          description: 'More managers needed',
        },
        {
          key: 'covered',
          tone: 'covered',
          label: 'Covered',
          description: 'Minimum reached',
        },
        {
          key: 'full',
          tone: 'full',
          label: 'At max',
          description: 'No more assignments',
        },
        {
          key: 'today',
          tone: 'today',
          label: 'Today',
          description: 'Current company date',
        },
        {
          key: 'locked',
          tone: 'locked',
          label: 'Locked',
          description: 'Read-only shift',
        },
      ]
    },
    gridByKey() {
      const cells = {}
      ;(this.dashboard ? this.dashboard.grid : []).forEach((cell) => {
        cells[`${cell.code}:${cell.date}`] = cell
      })
      return cells
    },
    gridStyle() {
      return {
        gridTemplateColumns: '150px repeat(7, minmax(104px, 1fr))',
      }
    },
    coveredCount() {
      if (!this.dashboard) return 0
      return this.dashboard.summary.shift_cells - this.dashboard.summary.deficit_cells
    },
    fullCount() {
      if (!this.dashboard) return 0
      return this.dashboard.grid.filter((cell) => cell.state === 'full').length
    },
    formatRange() {
      return `${format(parseISO(this.weekStart), 'd MMM')} – ${format(
        parseISO(this.weekEnd),
        'd MMM yyyy'
      )}`
    },
    roster() {
      return this.selectedShift ? this.selectedShift.roster || [] : []
    },
    rosterModalTitle() {
      if (!this.selectedCell) return 'Shift roster'
      return `${this.selectedCell.code} · ${format(
        parseISO(this.selectedCell.date),
        'EEE, d MMM yyyy'
      )}`
    },
    rosterCoverageLabel() {
      if (!this.selectedCell) return ''
      return {
        deficit: 'Below minimum',
        ok: 'Covered',
        full: 'At maximum',
      }[this.selectedCell.state] || 'Coverage'
    },
    selectedParticipantContext() {
      if (!this.participantId) return null
      return this.participantContexts[this.participantId] || null
    },
    rosterEditable() {
      return Boolean(this.selectedShift && this.selectedShift.is_editable)
    },
    availableParticipants() {
      if (!this.rosterEditable) return []
      const assignedIds = new Set(this.roster.map((item) => item.participant_id))
      return this.selectedParticipants.filter((participant) => {
        const context = this.participantContexts[participant.id]
        return context && context.is_editable &&
          !context.unavailable_reason && !assignedIds.has(participant.id)
      })
    },
    unavailableParticipants() {
      if (!this.rosterEditable) return []
      const assignedIds = new Set(this.roster.map((item) => item.participant_id))
      return this.selectedParticipants.filter((participant) => {
        const context = this.participantContexts[participant.id]
        return context && context.unavailable_reason && !assignedIds.has(participant.id)
      })
    },
    modalReasonRequired() {
      if (this.selectedParticipantContext) {
        return this.selectedParticipantContext.requires_reason
      }
      return Boolean(this.selectedShift && this.selectedShift.requires_reason)
    },
    hasOverrideReason() {
      return Boolean((this.overrideReason || '').trim())
    },
    modalLockedReason() {
      if (!this.selectedShift || this.selectedShift.is_editable) return ''
      return this.selectedShift.locked_reason || 'This shift is read-only.'
    },
    canAddAssignment() {
      const context = this.selectedParticipantContext
      if (!context || !context.is_editable || context.unavailable_reason) return false
      if (!this.selectedCell) return false
      if (context.requires_reason && !this.hasOverrideReason) return false
      return !this.saving
    },
    chartOptions() {
      if (!this.analytics.categories) return null
      return {
        accessibility: { enabled: false },
        chart: {
          type: 'spline',
          height: 350,
          backgroundColor: 'transparent',
          style: { fontFamily: 'CoreSansCR', fontSize: '14px' },
        },
        title: { text: null },
        credits: { enabled: false },
        legend: {
          align: 'left',
          itemDistance: 24,
          itemStyle: { color: '#344454', fontSize: '14px', fontWeight: '600' },
        },
        xAxis: {
          categories: this.analytics.categories,
          tickInterval: 2,
          lineColor: '#d8dee8',
          tickColor: '#d8dee8',
          labels: { style: { color: '#697386', fontSize: '14px' } },
        },
        yAxis: [
          {
            title: { text: 'Count', style: { color: '#697386', fontSize: '14px' } },
            min: 0,
            allowDecimals: false,
            gridLineColor: '#e6eaf0',
            labels: { style: { color: '#697386', fontSize: '14px' } },
          },
          {
            title: {
              text: 'Leads per manager',
              style: { color: '#697386', fontSize: '14px' },
            },
            min: 0,
            opposite: true,
            gridLineWidth: 0,
            labels: { style: { color: '#697386', fontSize: '14px' } },
          },
        ],
        plotOptions: {
          series: {
            lineWidth: 2.5,
            marker: { enabled: false, radius: 3 },
            states: { hover: { lineWidthPlus: 0 } },
          },
        },
        tooltip: {
          shared: true,
          valueDecimals: 1,
          style: { fontSize: '14px' },
        },
        series: (this.analytics.series || []).map((series) => ({
          ...series,
          type: 'spline',
          yAxis: series.key === 'leads_per_manager' ? 1 : 0,
          color: {
            new_leads: '#1769e0',
            scheduled_managers: '#147a55',
            leads_per_manager: '#9a5d00',
          }[series.key],
          dashStyle: series.key === 'leads_per_manager' ? 'ShortDash' : 'Solid',
        })),
      }
    },
  },
  created() {
    this.load()
  },
  mounted() {
    const layout = this.$el.closest('.default-layout')
    if (layout) layout.classList.add('shift-planner-layout')
    window.addEventListener('keydown', this.handleEscape)
  },
  beforeDestroy() {
    const layout = this.$el.closest('.default-layout')
    if (layout) layout.classList.remove('shift-planner-layout')
    window.removeEventListener('keydown', this.handleEscape)
  },
  methods: {
    positionRosterSelect(dropdownList, component, { width }) {
      dropdownList.classList.add('sp-roster-manager-options')
      dropdownList.style.width = width
      dropdownList.style.maxHeight = 'min(42vh, 420px)'
      dropdownList.style.zIndex = '10001'

      const popper = createPopper(component.$refs.toggle, dropdownList, {
        placement: 'bottom-start',
        modifiers: [
          {
            name: 'offset',
            options: { offset: [0, 4] },
          },
          {
            name: 'flip',
            options: { fallbackPlacements: ['top-start'] },
          },
          {
            name: 'preventOverflow',
            options: { padding: 12 },
          },
          {
            name: 'toggleClass',
            enabled: true,
            phase: 'write',
            fn({ state }) {
              component.$el.classList.toggle('drop-up', state.placement.startsWith('top'))
            },
          },
        ],
      })

      return () => popper.destroy()
    },
    focusRosterModal() {
      const header = this.$refs.rosterHeader
      if (header) header.focusClose()
    },
    handleEscape(event) {
      if (event.key === 'Escape' && this.selectedCell && !this.saving) {
        this.closeCell()
      }
    },
    errorText(error) {
      return (
        error?.response?.data?.details ||
        error?.response?.data?.detail ||
        'Request failed. Check your connection and try again.'
      )
    },
    cellFor(code, date) {
      return this.gridByKey[`${code}:${date}`] || null
    },
    coverageTone(state) {
      return { deficit: 'red', ok: 'green', full: 'neutral' }[state] || 'neutral'
    },
    coverageCellClasses(cell, day) {
      return {
        [`sp-coverage-cell--${cell ? cell.state : 'empty'}`]: true,
        'is-weekend': day.isWeekend,
        'is-today': day.date === this.todayDate,
        'is-selected': Boolean(
          cell && this.selectedCell && cell.shift_id === this.selectedCell.shift_id
        ),
      }
    },
    coverageCellAria(cell, template, day) {
      if (!cell) return `${template.code} on ${day.dayMonth}: unavailable`
      return `${template.code} on ${day.dayMonth}: ${cell.assigned} assigned, minimum ${cell.min}, maximum ${cell.max}`
    },
    participantLabel(participant) {
      const context = this.participantContexts[participant.id]
      const pendingLeave = context && context.leave_status === 'requested'
      return `${participant.user.name}${pendingLeave ? ' · leave requested' : ''}`
    },
    unavailableReason(participant) {
      const context = this.participantContexts[participant.id]
      if (context && context.unavailable_reason) return context.unavailable_reason
      return 'Unavailable for this shift.'
    },
    selectWeekDate(value) {
      if (!value) return
      this.weekStart = format(
        startOfWeek(parseISO(value), { weekStartsOn: 1 }),
        'yyyy-MM-dd'
      )
      this.closeCell()
      this.load()
    },
    async shiftWeek(direction) {
      if (this.weekNavigation) return
      this.weekNavigation = direction < 0 ? 'previous' : 'next'
      try {
        this.weekStart = format(
          addWeeks(parseISO(this.weekStart), direction),
          'yyyy-MM-dd'
        )
        this.closeCell()
        await this.load()
      } finally {
        this.weekNavigation = ''
      }
    },
    async goToCurrentWeek() {
      if (this.weekNavigation) return
      this.weekNavigation = 'current'
      try {
        this.weekStart = format(
          startOfWeek(new Date(), { weekStartsOn: 1 }),
          'yyyy-MM-dd'
        )
        this.closeCell()
        await this.load()
      } finally {
        this.weekNavigation = ''
      }
    },
    async load() {
      this.loading = true
      this.error = ''
      try {
        const dashboard = await getShiftPlannerDashboard({
          month: this.month,
          from_date: this.weekStart,
          to_date: this.weekEnd,
        })
        this.dashboard = dashboard.data
      } catch (error) {
        this.error = this.errorText(error)
      } finally {
        this.loading = false
      }
      await this.loadAnalytics()
    },
    async loadAnalytics() {
      this.analyticsError = ''
      try {
        const { data } = await getShiftPlannerDashboardAnalytics({
          month: this.month,
          from_date: this.weekStart,
          to_date: this.weekEnd,
        })
        this.analytics = data
      } catch (error) {
        this.analytics = {}
        this.analyticsError = this.errorText(error)
      }
    },
    async openCell(cell) {
      if (!cell) return
      this.selectedCell = cell
      this.participantId = null
      this.overrideReason = ''
      this.modalError = ''
      this.$modal.show('supervisor-roster')
      this.$nextTick(() => {
        const header = this.$refs.rosterHeader
        if (header) header.focusClose()
      })
      await this.loadSelectedRoster()
    },
    async loadSelectedRoster() {
      if (!this.selectedCell) return
      this.cellLoading = true
      try {
        const { data } = await getShiftPlannerRoster({
          shift_id: this.selectedCell.shift_id,
        })
        this.selectedShift = data.shifts[0] || null
        this.selectedParticipants = data.participants || []
        this.participantContexts = Object.fromEntries(
          (data.participant_contexts || []).map((context) => [
            context.participant_id,
            context,
          ])
        )
      } catch (error) {
        this.modalError = this.errorText(error)
      } finally {
        this.cellLoading = false
      }
    },
    closeCell() {
      this.$modal.hide('supervisor-roster')
      this.selectedCell = null
      this.selectedShift = null
      this.selectedParticipants = []
      this.participantContexts = {}
      this.participantId = null
      this.overrideReason = ''
      this.modalError = ''
    },
    contextForAssignment(assignment) {
      return this.participantContexts[assignment.participant_id] || null
    },
    removeBlockedReason(assignment) {
      const context = this.contextForAssignment(assignment)
      if (!context || !context.is_editable) {
        return (context && context.locked_reason) || this.modalLockedReason
      }
      if (context.requires_reason && !this.hasOverrideReason) {
        return 'Add a supervisor change reason first.'
      }
      return ''
    },
    canRemoveAssignment(assignment) {
      return !this.removeBlockedReason(assignment)
    },
    async addAssignment() {
      const context = this.selectedParticipantContext
      if (!this.canAddAssignment || !context) return
      const participant = this.selectedParticipants.find(
        (item) => item.id === this.participantId
      )
      if (!participant) return
      const rosterBefore = [...this.roster]
      const cellBefore = {
        assigned: this.selectedCell.assigned,
        state: this.selectedCell.state,
        deficit_cells: this.dashboard.summary.deficit_cells,
      }
      const optimisticAssignment = {
        id: `optimistic-${this.selectedCell.shift_id}-${this.participantId}`,
        participant_id: this.participantId,
        user: participant.user,
      }
      this.saving = true
      this.modalError = ''
      this.setSelectedRoster([...rosterBefore, optimisticAssignment])
      try {
        const { data } = await postShiftPlannerAssignment({
          shift_instance_id: this.selectedCell.shift_id,
          participant_id: this.participantId,
          reason: this.overrideReason,
        })
        this.setSelectedRoster(
          this.roster.map((item) => item === optimisticAssignment ? data : item
          )
        )
        this.updateContextVersions(data.week_version)
        this.participantId = null
      } catch (error) {
        this.restoreSelectedRoster(rosterBefore, cellBefore)
        this.modalError = this.errorText(error)
      } finally {
        this.saving = false
      }
    },
    async removeAssignment(assignment) {
      const context = this.contextForAssignment(assignment)
      if (!context || !this.canRemoveAssignment(assignment)) return
      const rosterBefore = [...this.roster]
      const cellBefore = {
        assigned: this.selectedCell.assigned,
        state: this.selectedCell.state,
        deficit_cells: this.dashboard.summary.deficit_cells,
      }
      this.saving = true
      this.modalError = ''
      this.setSelectedRoster(
        rosterBefore.filter((item) => item.id !== assignment.id)
      )
      try {
        const { data } = await postShiftPlannerAssignmentCancel(assignment.id, {
          reason: this.overrideReason,
        })
        this.updateContextVersions(data.week_version)
      } catch (error) {
        this.restoreSelectedRoster(rosterBefore, cellBefore)
        this.modalError = this.errorText(error)
      } finally {
        this.saving = false
      }
    },
    setSelectedRoster(roster) {
      if (!this.selectedShift || !this.selectedCell) return
      const previousState = this.selectedCell.state
      const assigned = roster.length
      let state = 'ok'
      if (assigned < this.selectedCell.min) state = 'deficit'
      else if (assigned >= this.selectedCell.max) state = 'full'
      this.$set(this.selectedShift, 'roster', roster)
      this.$set(this.selectedShift, 'assigned_count', assigned)
      this.$set(this.selectedShift, 'capacity_state', state)
      this.$set(this.selectedCell, 'assigned', assigned)
      this.$set(this.selectedCell, 'state', state)
      if (previousState !== state) {
        const delta = (state === 'deficit' ? 1 : 0) -
          (previousState === 'deficit' ? 1 : 0)
        this.$set(
          this.dashboard.summary,
          'deficit_cells',
          this.dashboard.summary.deficit_cells + delta
        )
      }
    },
    restoreSelectedRoster(roster, snapshot) {
      if (!this.selectedShift || !this.selectedCell) return
      this.$set(this.selectedShift, 'roster', roster)
      this.$set(this.selectedShift, 'assigned_count', snapshot.assigned)
      this.$set(this.selectedShift, 'capacity_state', snapshot.state)
      this.$set(this.selectedCell, 'assigned', snapshot.assigned)
      this.$set(this.selectedCell, 'state', snapshot.state)
      this.$set(
        this.dashboard.summary,
        'deficit_cells',
        snapshot.deficit_cells
      )
    },
    updateContextVersions(weekVersion) {
      if (!weekVersion) return
      Object.values(this.participantContexts).forEach((context) => {
        this.$set(context, 'week_version', weekVersion)
      })
    },
  },
}
</script>

<style lang="scss" src="./styles.scss"></style>
<style lang="scss" scoped>
.sp-week-switcher {
  display: grid;
  align-items: end;
  gap: 8px;
  grid-template-columns: 40px 180px 40px auto;
}

.sp-week-switcher__arrow {
  display: inline-flex;
  width: 40px;
  height: 40px;
  align-items: center;
  justify-content: center;
  border: 1px solid $ui-line-strong;
  border-radius: 8px;
  background: $ui-surface;
  color: $ui-blue;
  cursor: pointer;
}

.sp-week-switcher__arrow svg {
  width: 20px;
  height: 20px;
  fill: none;
  stroke: currentColor;
  stroke-linecap: round;
  stroke-linejoin: round;
  stroke-width: 2;
}

.sp-week-switcher__arrow:hover,
.sp-week-switcher__arrow:focus-visible {
  border-color: #8eb8ef;
  background: $ui-blue-soft;
  outline: 0;
}

.sp-week-switcher__current {
  min-height: $ui-control-height;
  padding: 0 $ui-space-12;
  border: 1px solid $ui-line-strong;
  border-radius: $ui-control-radius;
  background: $ui-surface-soft;
  color: $ui-ink;
  font: 500 $ui-font-size-control/1 $ui-font-body;
  cursor: pointer;
}

.sp-week-switcher__current:hover {
  background: $ui-blue-soft;
}

.sp-week-switcher__current:focus-visible {
  outline: 2px solid $ui-blue;
  outline-offset: 2px;
}

.sp-dashboard-metrics {
  grid-template-columns: repeat(4, minmax(180px, 1fr));
}
.sp-chart-card {
  margin-bottom: 0;
}

.sp-coverage-header {
  align-items: flex-start;
}

.sp-coverage-matrix__shift > i {
  width: 15px;
  height: 9px;
  border-radius: 3px;
  background: #dfe4eb;
}

.sp-coverage-matrix__shift > i.is-night {
  background: #6c5bc7;
}

.sp-coverage-matrix__shift > i.is-day {
  background: #4c8ce8;
}

.sp-coverage-scroll {
  min-width: 0;
  overflow-x: auto;
  padding: 2px 2px 8px;
}

.sp-coverage-navigation {
  display: grid;
  min-width: 0;
  align-items: center;
  grid-template-columns: 56px minmax(0, 1fr) 56px;
  gap: 12px;
}

.sp-coverage-nav-button {
  display: inline-flex;
  width: 56px;
  height: 96px;
  align-items: center;
  justify-content: center;
  border: 1px solid $ui-line-strong;
  border-radius: $ui-panel-radius;
  background: $ui-surface-soft;
  color: $ui-blue;
  cursor: pointer;
  transition: background-color .15s ease, border-color .15s ease;
}

.sp-coverage-nav-button:hover,
.sp-coverage-nav-button:focus-visible {
  border-color: $ui-blue;
  background: $ui-blue-soft;
  outline: 0;
}

.sp-coverage-nav-button svg {
  width: 28px;
  height: 28px;
  fill: none;
  stroke: currentColor;
  stroke-linecap: round;
  stroke-linejoin: round;
  stroke-width: 2;
}

.sp-coverage-matrix {
  display: grid;
  min-width: 980px;
  gap: 8px;
}

.sp-coverage-matrix__corner,
.sp-coverage-matrix__day,
.sp-coverage-matrix__shift {
  display: flex;
  min-height: 52px;
  align-items: center;
  padding: 8px 10px;
  color: $ui-muted;
  font-size: 14px;
}

.sp-coverage-matrix__corner {
  font-weight: 600;
}

.sp-coverage-matrix__day {
  justify-content: center;
  flex-direction: column;
  border-radius: 9px;
  text-align: center;
}

.sp-coverage-matrix__day strong {
  color: $ui-ink;
}

.sp-coverage-matrix__day.is-weekend strong {
  color: $ui-red;
}

.sp-coverage-matrix__day.is-today {
  background: #f4fbf7;
  box-shadow: inset 0 0 0 1px #53ad88;
}

.sp-coverage-matrix__shift {
  gap: 9px;
  border-radius: 9px;
  background: $ui-surface-soft;
}

.sp-coverage-matrix__shift div {
  display: grid;
}

.sp-coverage-matrix__shift strong {
  color: $ui-ink;
}

.sp-coverage-matrix__shift span {
  color: $ui-muted;
  font-size: 14px;
}

.sp-coverage-cell {
  display: flex;
  min-height: 68px;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  padding: 9px;
  border: 1px solid $ui-line-strong;
  border-radius: 10px;
  background: #f6f8fb;
  color: $ui-ink;
  cursor: pointer;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
}

.sp-coverage-cell strong {
  font-size: 20px;
}

.sp-coverage-cell span {
  color: $ui-muted;
  font-size: 14px;
}

.sp-coverage-cell--deficit {
  border-color: #efb2ba;
  background: $ui-red-soft;
}

.sp-coverage-cell--ok {
  border-color: #a9dec9;
  background: $ui-green-soft;
}

.sp-coverage-cell--full {
  border-color: #b8c0cc;
  background: #eef1f5;
}

.sp-coverage-cell.is-weekend:not(.sp-coverage-cell--deficit):not(.sp-coverage-cell--ok) {
  background: #fff8f9;
}

.sp-coverage-cell.is-today {
  box-shadow: inset 0 0 0 1px #53ad88;
}

.sp-coverage-cell:hover:not(:disabled),
.sp-coverage-cell.is-selected {
  border-color: #8eb8ef;
  box-shadow: 0 0 0 2px rgba(23, 105, 224, 0.1);
}

.sp-coverage-cell:focus-visible {
  outline: 2px solid #8eb8ef;
  outline-offset: 2px;
}

.sp-coverage-cell:disabled {
  cursor: default;
  opacity: 0.35;
}

.sp-chart-card__heading {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
}

.sp-chart-shell {
  margin-top: 16px;
  padding: 12px 12px 4px;
  border: 1px solid $ui-line;
  border-radius: 12px;
  background: $ui-surface-soft;
  overflow: hidden;
}

.sp-roster-modal {
  max-height: calc(100vh - 64px);
}

.sp-roster-modal--editable {
  min-height: 760px;
}

.sp-roster-modal .sp-modal__body {
  flex: 1 1 auto;
}

.sp-roster-overview {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 10px;
  margin-bottom: 14px;
  color: $ui-muted;
  font-size: 14px;
}

.sp-roster-lock,
.sp-roster-note {
  display: flex;
  align-items: center;
  gap: 9px;
  margin-bottom: 14px;
  padding: 10px 12px;
  border: 1px solid $ui-line-strong;
  border-radius: 9px;
  background: $ui-surface-soft;
  color: $ui-muted;
  font-size: 14px;
}

.sp-roster-lock svg {
  width: 18px;
  height: 18px;
  fill: none;
  stroke: currentColor;
  stroke-linecap: round;
  stroke-linejoin: round;
  stroke-width: 1.8;
}

.sp-roster-note {
  border-color: #c8bdeb;
  background: #f5f2ff;
  color: #55469f;
}

.sp-roster-columns {
  display: grid;
  align-items: start;
  grid-template-columns: minmax(0, 2fr) minmax(280px, 1fr);
  gap: 24px;
}

.sp-roster-columns__lists,
.sp-roster-editor {
  min-width: 0;
}

.sp-roster-editor {
  display: grid;
  gap: 20px;
  padding-left: 24px;
  border-left: 1px solid $ui-line;
}

.sp-roster-editor > label {
  display: grid;
  gap: 6px;
}

.sp-roster-read-only {
  display: grid;
  justify-items: start;
  gap: 8px;
  padding: 16px;
  border: 1px solid $ui-line-strong;
  border-radius: $ui-panel-radius;
  background: $ui-surface-soft;
}

.sp-roster-read-only svg {
  width: 24px;
  height: 24px;
  margin-bottom: 4px;
  fill: none;
  stroke: $ui-muted;
  stroke-linecap: round;
  stroke-linejoin: round;
  stroke-width: 1.8;
}

.sp-roster-read-only strong {
  color: $ui-ink;
  font-size: 16px;
}

.sp-roster-read-only p {
  margin: 0;
  color: $ui-ink;
}

.sp-roster-read-only span {
  color: $ui-muted;
  font-size: 14px;
}

.sp-roster-section + .sp-roster-section {
  margin-top: 18px;
}

.sp-roster-section__heading {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 10px;
}

.sp-roster-section__heading h3 {
  margin: 0;
  color: $ui-ink;
  font-size: 16px;
}

.sp-roster-section__heading span {
  color: $ui-muted;
  font-size: 14px;
}

.sp-roster-list {
  display: grid;
}

.sp-roster-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 12px 0;
  border-bottom: 1px solid $ui-line;
}

.sp-roster-item__manager {
  display: grid;
  min-width: 0;
  gap: 4px;
}

.sp-roster-item__manager strong {
  color: $ui-ink;
}

.sp-roster-form {
  display: grid;
  gap: 15px;
}

.sp-unavailable-list {
  display: grid;
  overflow: hidden;
  border: 1px solid $ui-line;
  border-radius: $ui-panel-radius;
}

.sp-unavailable-item {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(180px, 1fr);
  gap: $ui-space-16;
  padding: $ui-space-12 $ui-space-16;
  border-bottom: 1px solid $ui-line;
  background: $ui-surface-soft;
}

.sp-unavailable-item:last-child {
  border-bottom: 0;
}

.sp-unavailable-item > div {
  min-width: 0;
}

.sp-unavailable-item > span {
  color: $ui-red;
  font-size: $ui-font-size-body;
  line-height: 1.45;
}

.sp-roster-save-state {
  color: $ui-muted;
  font-size: 14px;
}

@media (max-width: 1050px) {
  .sp-dashboard-metrics {
    grid-template-columns: repeat(2, minmax(180px, 1fr));
  }
}

@media (max-width: 750px) {
  .sp-week-switcher {
    width: 100%;
    grid-template-columns: 40px minmax(150px, 1fr) 40px;
  }
  .sp-week-switcher__current {
    grid-column: 1 / -1;
  }
  .sp-week-datepicker {
    width: 100%;
  }
}

@media (max-width: 560px) {
  .sp-dashboard-metrics {
    grid-template-columns: 1fr;
    gap: 10px;
  }
  .sp-roster-item {
    align-items: stretch;
    flex-direction: column;
  }
  .sp-unavailable-item {
    grid-template-columns: 1fr;
    gap: $ui-space-8;
  }
  .sp-roster-item .sp-button,
  .sp-roster-form > .sp-button {
    width: 100%;
  }
}

@media (max-width: 900px) {
  .sp-coverage-navigation {
    grid-template-columns: 40px minmax(0, 1fr) 40px;
    gap: 6px;
  }

  .sp-coverage-nav-button {
    width: 40px;
    height: 72px;
  }

  .sp-roster-columns {
    grid-template-columns: minmax(0, 1fr);
  }

  .sp-roster-editor {
    padding-top: 20px;
    padding-left: 0;
    border-top: 1px solid $ui-line;
    border-left: 0;
  }
}

@media (max-height: 824px) {
  .sp-roster-modal--editable {
    min-height: calc(100vh - 64px);
  }
}
</style>
